import { MenuOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, MenuProps, Row } from 'antd';
import { MenuItemType } from 'antd/lib/menu/interface';
import { useLogoutMutation } from 'api/AuthApi';
import { UserAccessLevelPlanEnum } from 'api/UsersApi';
import LogoComponent from 'components/Common/LogoComponent/LogoComponent';
import ContainerComponent from 'components/Tool/ContainerComponent/ContainerComponent';
import UserAvatar from 'components/UiKit/UserAvatar/UserAvatar';
import { useCompanyContext } from 'contexts/CompanyContext';
import OrganizationInvite from 'pages/Client/Header/OrganizationInvite';
import { useSelector } from 'react-redux';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { logout as logoutAction, selectAuthUser } from 'store/authSlice';
import { useAppDispatch } from 'store/store';
import { securityLogger } from 'utils/securityLogger';
import { getAccessLevelPlanName, getCurrentUrl, hasOrganizationSponsoredUnlimitedAccess } from 'utils/utils';
import './Header.css';

function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { companyConfig } = useCompanyContext();

  const user = useSelector(selectAuthUser);
  const hasOrgSponsoredAccess = hasOrganizationSponsoredUnlimitedAccess(user);

  const [logoutRequest] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logoutRequest().unwrap();
    } catch (e) {
      securityLogger.error('Logout request failed', e);
      dispatch(logoutAction());
    }
  };

  const isHomeOrLifeStoryNavContext =
    pathname === '/home' ||
    pathname === '/app/life-story' ||
    pathname === '/app/artifacts' ||
    pathname === '/shared-with-me' ||
    pathname.startsWith('/app/shared-with-me/artifacts') ||
    pathname.startsWith('/app/shared-with-me/life-stories') ||
    pathname === '/inbox' ||
    pathname === '/life-review' ||
    pathname === '/life-review/session';
  const isMessagesNavContext = pathname.startsWith('/message');
  const isDashboardOrDropdownNavContext =
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/pricing') ||
    pathname.startsWith('/account') ||
    pathname.startsWith('/billing') ||
    pathname.startsWith('/beneficiaries') ||
    pathname.startsWith('/contacts') ||
    pathname.startsWith('/message-templates') ||
    pathname.startsWith('/gift') ||
    pathname.startsWith('/faq') ||
    pathname.startsWith('/contact-us') ||
    pathname.startsWith('/sysadmin');

  const homeLifeStoryMenu: MenuProps['items'] = [
    {
      label: <NavLink to="/home">HOME</NavLink>,
      key: 'home'
    },
    {
      label: <NavLink to="/app/life-story">LIFE STORY</NavLink>,
      key: 'life-story'
    },
    {
      label: <NavLink to="/message">MESSAGES</NavLink>,
      key: 'messages'
    },
    {
      label: <NavLink to="/app/artifacts">ARTIFACTS</NavLink>,
      key: 'artifacts'
    }
  ];

  const defaultMainMenu: MenuProps['items'] = [
    {
      label: <NavLink to="/">Home</NavLink>,
      key: 'home'
    },
    {
      label: <NavLink to="/app/artifacts">Artifacts</NavLink>,
      key: 'artifacts'
    },
    {
      label: <NavLink to="/message-templates">Example Messages</NavLink>,
      key: 'templates'
    }
  ];

  const mainMenu =
    isMessagesNavContext || isHomeOrLifeStoryNavContext || isDashboardOrDropdownNavContext ? homeLifeStoryMenu : defaultMainMenu;

  const subMenu: MenuProps['items'] = [
    ...(user?.isSysAdmin
      ? [
          {
            label: (
              <Link to="/sysadmin">
                <strong>SysAdmin</strong>
              </Link>
            ),
            key: 'sysadmin'
          }
        ]
      : []),
    {
      label: <Link to="/dashboard">Dashboard</Link>,
      key: 'dashboard'
    },
    {
      label: <Link to="/shared-with-me">Shared With Me</Link>,
      key: 'shared-with-me'
    },
    {
      label: <Link to="/account">Account</Link>,
      key: 'account'
    },
    ...(companyConfig.isEnterprise
      ? []
      : hasOrgSponsoredAccess
      ? []
      : [
          {
            label: <Link to="/billing">Billing</Link>,
            key: 'billing'
          }
        ]),
    ...(user?.isAdmin
      ? [
          {
            label: <Link to="/beneficiaries">Administration</Link>,
            key: 'administration'
          }
        ]
      : []),
    {
      label: <Link to="/contacts">Contacts</Link>,
      key: 'contacts'
    },
    {
      label: <Link to="/message-templates">Example Messages</Link>,
      key: 'dropdown-templates'
    },
    ...(companyConfig.isEnterprise
      ? []
      : [
          {
            label: <Link to="/gift">Gift</Link>,
            key: 'gift'
          }
        ]),
    {
      type: 'divider'
    },
    {
      label: <Link to="/faq">FAQ</Link>,
      key: 'faq'
    },
    {
      label: <Link to="/contact-us">Contact Us</Link>,
      key: 'contact-us'
    },
    {
      type: 'divider'
    },
    {
      label: <div onClick={handleLogout}>Logout</div>,
      key: 'logout',
      danger: true
    }
  ];

  return (
    <>
      <div className="header">
        {user ? (
          <ContainerComponent>
            <>
              <div className="header-mobile">
                <Row justify="space-between" align="middle" wrap={false}>
                  <Col className="header-mobile-logo">
                    <NavLink to="/">
                      <LogoComponent maxHeight={36} />
                    </NavLink>
                  </Col>
                  <Col className="text-right header-mobile-menu" data-tour="get-credits-mobile">
                    <Dropdown
                      menu={{
                        items: [
                          ...mainMenu,
                          {
                            type: 'divider'
                          },
                          ...subMenu
                        ]
                      }}
                      trigger={['click']}
                    >
                      <div className="user-button" data-tour="menu-mobile">
                        <UserAvatar size={28} user={user} />
                        <MenuOutlined />
                      </div>
                    </Dropdown>
                  </Col>
                </Row>
              </div>

              <div className="header-desktop">
                <Row align="middle" wrap={false}>
                  <Col span={16}>
                    <Row align="middle" wrap={false}>
                      <Col>
                        <NavLink to="/">
                          <LogoComponent maxHeight={36} />
                        </NavLink>
                      </Col>
                      <Col>
                        <ul className="menu" data-tour="menu">
                          {mainMenu.map((item) => (
                            <li key={(item as MenuItemType).key}>{(item as MenuItemType).label}</li>
                          ))}
                        </ul>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={8}>
                    <div className="right">
                      {!companyConfig.isEnterprise && !hasOrgSponsoredAccess && (
                        <div className="credits" data-tour="get-credits">
                          {user.accessLevelPlan === UserAccessLevelPlanEnum.Free ? (
                            <Link to="/pricing">
                              <strong>Upgrade Plan</strong>
                            </Link>
                          ) : (
                            <Link to="/pricing" className="credits-count">
                              {getAccessLevelPlanName(user.accessLevelPlan)}
                            </Link>
                          )}
                        </div>
                      )}
                      <Dropdown menu={{ items: subMenu }} trigger={['click']}>
                        <div className="user-button">
                          <UserAvatar size={28} user={user} />
                          <MenuOutlined />
                        </div>
                      </Dropdown>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          </ContainerComponent>
        ) : (
          <ContainerComponent>
            <div className="header-public">
              <Row align="middle" wrap={false}>
                <Col span={16}>
                  <Row align="middle" wrap={false}>
                    <Col>
                      <NavLink to="/">
                        <LogoComponent variant="vertical" maxHeight={72} />
                      </NavLink>
                    </Col>
                  </Row>
                </Col>
                <Col span={8}>
                  <div className="right">
                    <Button
                      type="primary"
                      onClick={() =>
                        navigate('/login', {
                          state: { redirectUrl: getCurrentUrl() }
                        })
                      }
                    >
                      Sign In
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </ContainerComponent>
        )}
      </div>

      <OrganizationInvite />
    </>
  );
}

export default Header;
